import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { Geolocation } from '@capacitor/geolocation'
import { DejavooTop } from 'capacitor-dejavoo-top'
import { DEJAVOO_TOP_LOG_PREFIX } from './DejavooTop.js'

/** Logs one line; avoid passing objects that may contain `cfg` or credentials. */
export function topCheckoutLog (phase, detail) {
  try {
    if (detail !== undefined && detail !== null && typeof detail === 'object') {
      console.log(`${DEJAVOO_TOP_LOG_PREFIX} ${phase} ${JSON.stringify(detail)}`)
    } else {
      console.log(`${DEJAVOO_TOP_LOG_PREFIX} ${phase}`, detail)
    }
  } catch {
    console.log(`${DEJAVOO_TOP_LOG_PREFIX} ${phase}`, String(detail))
  }
}

/** Capacitor / bridge errors often omit `message` or nest it. */
export function pluginErrorMessage (err) {
  if (err == null) {
    return ''
  }
  if (typeof err === 'string') {
    return err.trim()
  }
  const m = err.message ?? err.errorMessage ?? err.msg
  if (typeof m === 'string' && m.trim()) {
    return m.trim()
  }
  if (typeof err.toString === 'function') {
    const s = String(err.toString())
    if (s && s !== '[object Object]') {
      return s.trim()
    }
  }
  return ''
}

/** Last 4 chars for logs; never log full TPN / merchant code. */
export function maskDejavooTopIdent (value) {
  if (value == null || value === '') {
    return '—'
  }
  const t = String(value).trim()
  if (t.length <= 4) {
    return '****'
  }
  return `***${t.slice(-4)}`
}

/**
 * @returns {Promise<import('capacitor-dejavoo-top').DejavooTopNativeDiagnostics|null>}
 */
export async function fetchDejavooTopNativeDiagnostics () {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    return null
  }
  try {
    if (typeof DejavooTop.getDiagnostics === 'function') {
      return await DejavooTop.getDiagnostics()
    }
    return await DejavooTop.isAvailable()
  } catch (e) {
    topCheckoutLog('nativeDiagnostics.failed', { err: e?.message })
    return null
  }
}

/**
 * Rows for Session debug panel (iOS native Tap to Pay).
 * @returns {Promise<Array<{ key: string, value: string }>>}
 */
export async function dejavooTopNativeDiagnosticsRows () {
  const d = await fetchDejavooTopNativeDiagnostics()
  if (!d) {
    return []
  }
  const fmt = (v) => (v === undefined || v === null || v === '' ? '—' : String(v))
  return [
    { key: 'IPOS_ENVIRONMENT (Info.plist)', value: fmt(d.iposEnvironment) },
    { key: 'IPOS_ENV plist unresolved', value: fmt(d.iposEnvironmentPlistUnresolved) },
    { key: 'Bundle ID', value: fmt(d.bundleId) },
    { key: 'iOS version', value: fmt(d.iosVersion) },
    { key: 'Device model', value: fmt(d.deviceModel) },
    { key: 'ToP device supported', value: fmt(d.tapToPayDeviceSupported) },
    { key: 'ToP unsupported reason', value: fmt(d.tapToPayUnsupportedReason) },
    { key: 'hw.machine', value: fmt(d.hwMachine) }
  ]
}

/**
 * Query Apple (via Iposgo checkDeviceConfiguration) for merchant Tap to Pay acceptance.
 * @param {object} cfg
 * @returns {Promise<{ parametersDownloaded: boolean, merchantAccepted: boolean, ready: boolean, sdkSessionActive?: boolean }|null>}
 */
export async function refreshIosMerchantAcceptance (cfg) {
  if (Capacitor.getPlatform() !== 'ios' || !cfg?.tpn || !cfg?.merchant_code) {
    return null
  }
  try {
    return await DejavooTop.refreshMerchantAcceptanceStatus({
      tpn: cfg.tpn,
      merchantCode: cfg.merchant_code
    })
  } catch (e) {
    topCheckoutLog('ios.refreshMerchantAcceptance.failed', { err: pluginErrorMessage(e) })
    return {
      parametersDownloaded: false,
      merchantAccepted: false,
      ready: false,
      sdkSessionActive: false
    }
  }
}

/**
 * Log native + API context before register / warm (no secrets).
 * @param {object|null} cfg from loadDejavooTopConfig
 */
/**
 * Restore Iposgo reader session after cold start (downloadParameter) when wizard was completed earlier.
 * @returns {Promise<{ ok: boolean, needsSetup?: boolean, err?: string }>}
 */
export async function ensureIosTopSdkSession (cfg) {
  if (Capacitor.getPlatform() !== 'ios' || !cfg?.tpn || !cfg?.merchant_code) {
    return { ok: false, err: 'incomplete_config' }
  }
  const { prepareIosTopCheckout } = await import('./iosTopCheckoutReadiness.js')
  const prep = await prepareIosTopCheckout({ purpose: 'warm', skipBind: true })
  if (!prep.ok) {
    return {
      ok: false,
      needsSetup: prep.needsSetup === true,
      err: prep.err || prep.message || 'prepare_failed'
    }
  }
  return { ok: true }
}

export async function logDejavooTopRegistrationContext (cfg) {
  const native = await fetchDejavooTopNativeDiagnostics()
  topCheckoutLog('registration.context', {
    iposEnvironment: native?.iposEnvironment ?? null,
    iposEnvironmentPlistUnresolved: native?.iposEnvironmentPlistUnresolved ?? null,
    bundleId: native?.bundleId ?? null,
    apiEnvironment: cfg?.environment ?? null,
    apiPlatform: cfg?.platform ?? null,
    tpnSuffix: maskDejavooTopIdent(cfg?.tpn),
    merchantSuffix: maskDejavooTopIdent(cfg?.merchant_code)
  })
}

/**
 * @param {{ requireNfc?: boolean, availability: object|null }} options
 */
export async function ensureGeolocationForTop () {
  try {
    let geo = await Geolocation.checkPermissions()
    if (geo.location !== 'granted' && geo.coarseLocation !== 'granted') {
      geo = await Geolocation.requestPermissions()
    }
    const granted =
      geo.location === 'granted'
      || geo.coarseLocation === 'granted'
    topCheckoutLog('geolocation.permission', {
      location: geo.location,
      coarseLocation: geo.coarseLocation,
      granted
    })
    return granted
  } catch (e) {
    topCheckoutLog('geolocation.failed', { err: e?.message })
    return false
  }
}

/**
 * @param {{ platform?: 'ios' | 'android' }} [options]
 */
export async function loadDejavooTopConfig (options = {}) {
  const { businessInfoService } = await import('./BusinessInfoService')
  return businessInfoService.getDejavooTopConfig(options)
}

export async function registerMobileTerminalForTop (cfg) {
  const { paymentService } = await import('./PaymentService')
  const { identifier: deviceId } = await Device.getId()
  const info = await Device.getInfo()
  topCheckoutLog('device.info', { model: info.model, osVersion: info.osVersion })
  topCheckoutLog('registerMobileTerminal.request')
  const terminalRes = await paymentService.registerDejavooMobileTerminal({
    tpn: cfg.tpn,
    device_id: deviceId,
    device_model: info.model,
    os_version: info.osVersion,
    app_version: info.webViewVersion
  })
  topCheckoutLog('registerMobileTerminal.response', {
    success: terminalRes?.success,
    hasTerminalId: !!terminalRes?.terminal?.id,
    hasError: !!terminalRes?.error
  })
  return terminalRes
}

export function buildTopSaleExtraPayload ({ saleId, saleRefForSdk }) {
  return {
    applicationType: 'DVPAYLITE',
    refId: String(saleRefForSdk ?? saleId),
    receiptType: 'No',
    isTxnStatusScreenRequired: 'Yes',
    isReceiptsNeeded: false,
    showApprovalScreen: false
  }
}

/**
 * Record an approved Tap to Pay transaction against the sale.
 */
export async function recordTopPayment ({
  saleId,
  payAmount,
  taxAmount,
  terminalId,
  splitLegIndex,
  txn
}) {
  const { paymentService } = await import('./PaymentService')
  const gatewayMetadata = {
    status: txn.status,
    message: txn.message,
    card_type: txn.cardType,
    last4: txn.last4,
    invoice_no: txn.invoiceNo,
    raw: txn.raw
  }
  if (splitLegIndex != null) {
    topCheckoutLog('processSplitLeg.request', { saleId, terminalId, splitLegIndex })
    return paymentService.processSplitLeg({
      saleId,
      legIndex: splitLegIndex,
      method: 'processed_externally',
      amount: payAmount,
      taxAmount,
      terminalId,
      notes: 'Dejavoo Tap to Pay split leg',
      processedExternally: true,
      gatewayReference: txn.transactionId,
      gatewayMetadata
    })
  }
  topCheckoutLog('processCardPayment.request', { saleId, terminalId })
  return paymentService.processCardPayment({
    saleId,
    amount: payAmount,
    tipAmount: 0,
    taxAmount,
    terminalId,
    notes: 'Dejavoo Tap to Pay',
    processedExternally: true,
    gatewayReference: txn.transactionId,
    gatewayMetadata
  })
}

export function validateTopCheckoutInputs ({ saleId, amount }) {
  if (saleId == null || saleId === '') {
    return {
      ok: false,
      handled: true,
      success: false,
      message: 'Sale was not created. Go back and try Card again.'
    }
  }
  const payAmount = Number(amount)
  if (!Number.isFinite(payAmount) || payAmount <= 0) {
    return {
      ok: false,
      handled: true,
      success: false,
      message: 'Invalid payment total for Tap to Pay. Wait for totals to finish updating, then try again.'
    }
  }
  return { ok: true, payAmount }
}
